import { useEffect, useState } from 'react'
import { motion } from 'motion/react'
import { Check, Zap } from 'lucide-react'
import type { AIAgent, Workflow } from '@/data/industries'
import { cn } from '@/lib/utils'
import { shortTime } from './listing'

const OUT_EASE = [0.22, 1, 0.36, 1] as const
const WIPE_EASE = [0.76, 0, 0.24, 1] as const
const halftone = (alpha: number, pitch: number): React.CSSProperties => ({
  backgroundImage: `radial-gradient(rgba(255,255,255,${alpha}) 0.9px, transparent 1.3px)`,
  backgroundSize: `${pitch}px ${pitch}px`,
})

/** The agent a step names ("Marcus analyzes performance" → Marcus), if any. */
export function stepAgent(step: string, agents: AIAgent[]): AIAgent | undefined {
  return agents.find((a) => step.startsWith(`${a.name} `))
}

/** Step x offsets (% of the square): a gentle zig-zag down the stage. */
const STEP_X = [9, 20, 9, 20]
const STEP_TOP = 25
const STEP_GAP = 13.5

/**
 * A workflow drawn as a diagram for the pinned stage: the trigger chip at
 * the top, the agent steps stepping down a blueprint grid on a connector,
 * and the output tile (purple, halftone) with the time saved. Sized in
 * container units so it scales with the stage. Nodes settle in once, the
 * first time the step is active; the connector opens with a clip-path.
 */
export function WorkflowDiagram({
  workflow,
  agents,
  active,
}: {
  workflow: Workflow
  agents: AIAgent[]
  active: boolean
}) {
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    if (active) setSeen(true)
  }, [active])

  const steps = workflow.steps.slice(0, 4)
  const lastTop = STEP_TOP + (steps.length - 1) * STEP_GAP

  const node = (i: number) => ({
    initial: false as const,
    animate: seen ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 },
    transition: { duration: 0.55, ease: OUT_EASE, delay: seen ? 0.25 + i * 0.1 : 0 },
  })

  return (
    <div
      aria-hidden
      className="relative h-full w-full overflow-hidden bg-[#f3f3f3] [container-type:inline-size] dark:bg-[#15151d]"
      style={{
        backgroundImage:
          'linear-gradient(to right, rgb(10 10 10 / 0.06) 1px, transparent 1px), linear-gradient(to bottom, rgb(10 10 10 / 0.06) 1px, transparent 1px)',
        backgroundSize: '5% 5%',
      }}
    >
      {/* Connector: spine from the trigger through each step, then across to the output */}
      <motion.svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
        initial={false}
        animate={{ clipPath: seen ? 'inset(0% 0% 0% 0%)' : 'inset(0% 0% 100% 0%)' }}
        transition={{ duration: 1.1, ease: WIPE_EASE, delay: seen ? 0.15 : 0 }}
      >
        <path
          d={`M 14 13 V ${lastTop + 5} H 58 V 70`}
          fill="none"
          stroke="var(--color-landing-secondary)"
          strokeWidth="2"
          strokeDasharray="4 4"
          vectorEffect="non-scaling-stroke"
        />
      </motion.svg>

      {/* Trigger */}
      <motion.div
        className="absolute flex items-center gap-[1.6cqw] bg-[#0a0a0a] px-[2.2cqw] py-[1.8cqw] text-white shadow-[0_18px_36px_-16px_rgba(10,10,10,0.5)]"
        style={{ left: '7%', top: '6%', maxWidth: '72%' }}
        {...node(0)}
      >
        <Zap className="h-[3.2cqw] w-[3.2cqw] shrink-0 text-[#ff00f2]" strokeWidth={2.25} />
        <span className="flex min-w-0 flex-col">
          <span className="text-[max(8px,1.7cqw)] font-bold uppercase tracking-[0.08em] text-white/70">Trigger</span>
          <span className="truncate font-[family-name:var(--font-landing-subdisplay)] text-[max(10px,2.9cqw)] font-bold leading-tight">
            {workflow.trigger}
          </span>
        </span>
      </motion.div>

      {/* Steps */}
      {steps.map((step, i) => {
        const agent = stepAgent(step, agents)
        return (
          <motion.div
            key={`${step}-${i}`}
            className="absolute flex items-center gap-[1.8cqw] border border-[var(--color-landing-gray-200)] bg-white px-[1.8cqw] py-[1.5cqw] shadow-[0_14px_28px_-18px_rgba(10,10,10,0.45)] dark:border-[var(--color-landing-gray-700)] dark:bg-[#1c1c26]"
            style={{ left: `${STEP_X[i]}%`, top: `${STEP_TOP + i * STEP_GAP}%`, width: '58%' }}
            {...node(i + 1)}
          >
            <span
              className={cn(
                'flex h-[6.4cqw] w-[6.4cqw] shrink-0 items-center justify-center font-[family-name:var(--font-landing-display)] text-[max(9px,2.6cqw)] text-white'
              )}
              style={{ backgroundColor: agent ? (i % 2 ? '#ff00f2' : 'var(--color-landing-accent)') : '#0a0a0a' }}
            >
              {agent ? agent.name[0] : <Check className="h-[3.4cqw] w-[3.4cqw]" strokeWidth={3} />}
            </span>
            <span className="min-w-0 truncate font-[family-name:var(--font-landing-subdisplay)] text-[max(10px,2.6cqw)] font-semibold leading-tight text-[var(--color-landing-gray-900)] dark:text-white">
              {step}
            </span>
          </motion.div>
        )
      })}

      {/* Output */}
      <motion.div
        className="absolute flex flex-col justify-between p-[2.6cqw] text-white shadow-[0_24px_48px_-16px_rgba(178,0,255,0.45)]"
        style={{ right: '6%', bottom: '6%', width: '44%', height: '26%', backgroundColor: 'var(--color-landing-secondary)' }}
        {...node(steps.length + 1)}
      >
        <span className="absolute inset-0" style={halftone(0.24, 5)} />
        <span className="relative text-[max(8px,1.7cqw)] font-bold uppercase tracking-[0.08em] text-white/80">Output</span>
        <span className="relative font-[family-name:var(--font-landing-subdisplay)] text-[max(10px,2.9cqw)] font-bold leading-tight">
          {workflow.output}
        </span>
        <span className="relative inline-block self-start bg-[#ff00f2] px-[1.2cqw] py-[0.5cqw] text-[max(8px,1.8cqw)] font-bold uppercase tracking-[0.04em] text-[#0a0a0a]">
          {shortTime(workflow.timeSaved)}
        </span>
      </motion.div>
    </div>
  )
}
